import { useLayoutEffect, useRef, useState } from 'react'
import buildMaterialDotPlot from '../../helpers/intelligence/buildMaterialDotPlot'
import { MIN_COMPANIES_TO_COMPARE } from '../../helpers/intelligence/buildMaterialContextRows'

// Maten in pixels: de grafiek tekent op de gemeten breedte van zijn blok, dus
// één eenheid is één pixel en de tekst schaalt niet mee met een breed scherm.
// Tot de meting er is (en in jsdom, dat geen layout doet) geldt de standaard.
const STANDAARD_BREEDTE = 900
const AS_LINKS = 240
const WAARDE_KOLOM = 160
const KOP = 30
const RIJ = 26
const BOVEN = 26
const TUSSENRUIMTE = 10

/**
 * Stippengrafiek van een materialen-benchmark per contextkenmerk: per vraag een
 * stip per antwoordgroep, met een streep naar de benchmarklijn. Rechts van de
 * lijn is meer besparing dan gemiddeld.
 *
 * Eén kleur; 'te weinig organisaties' is een vorm (open stip), geen kleur. De
 * exacte cijfers staan in de tabellen eronder — dat is ook de tabelweergave
 * voor wie de grafiek niet kan zien.
 *
 * Tekst krijgt zijn maat via een CSS-klasse: de globale `text`-regel overschrijft
 * het fontSize-attribuut van SVG, en `var()` werkt niet in een
 * presentatieattribuut.
 * AI-CONTEXT: See docs/patterns.md#ui-color-tokens
 *
 * @param {object} props
 * @param {import('../../helpers/intelligence/buildMaterialContextRows').MaterialContextSection[]} props.sections - Uit buildMaterialContextRows
 * @param {number|null} props.reference - Benchmark van de basis (energiearmoede-organisaties)
 * @param {string} props.unit - Korte eenheid achter een waarde, bijvoorbeeld 'kg' of '€'
 */
const MaterialContextDotPlot = ({ sections, reference, unit }) => {
    /** @type {React.RefObject<HTMLDivElement>} */
    const ref = useRef(null)
    const [breedte, setBreedte] = useState(STANDAARD_BREEDTE)

    // Meten vóór de eerste paint, daarna bijhouden bij elke maatwijziging.
    // ResizeObserver ontbreekt in jsdom; dan blijft de standaard staan.
    useLayoutEffect(() => {
        const el = ref.current
        if (!el) return undefined
        const meet = () => { if (el.clientWidth > 0) setBreedte(el.clientWidth) }
        meet()
        if (typeof ResizeObserver === 'undefined') return undefined
        const observer = new ResizeObserver(meet)
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    const { sections: vragen, max } = buildMaterialDotPlot(sections, reference)
    if (!vragen.length) return null

    const AS_RECHTS = breedte - WAARDE_KOLOM
    const x = (v) => AS_LINKS + (v / max) * (AS_RECHTS - AS_LINKS)
    // Ronde stappen; max is altijd een veelvoud van 50.
    const stap = max <= 300 ? 50 : 100
    const ticks = Array.from({ length: Math.floor(max / stap) + 1 }, (_, i) => i * stap)

    let y = BOVEN
    const blokken = vragen.map((vraag) => {
        const kopY = y
        y += KOP
        const rijen = vraag.rows.map((rij) => {
            const cy = y + RIJ / 2
            y += RIJ
            return { rij, cy }
        })
        const rijenOnder = y
        y += TUSSENRUIMTE
        // Lijnen alleen langs de antwoorden: de titel loopt over de volle
        // breedte en mag er niet doorheen gekruist worden.
        return { vraag, kopY, rijen, rijenBoven: kopY + KOP, rijenOnder }
    })
    const onder = y

    return (
        <div className="material-dotplot" ref={ref}>
            <div className="material-dotplot__legend">
                <span className="material-dotplot__legend-item">
                    <svg className="material-dotplot__swatch" viewBox="0 0 12 12" aria-hidden="true">
                        <circle className="material-dotplot__swatch-dot" cx="6" cy="6" r="5" />
                    </svg>
                    {`${MIN_COMPANIES_TO_COMPARE} of meer organisaties`}
                </span>
                <span className="material-dotplot__legend-item">
                    <svg className="material-dotplot__swatch" viewBox="0 0 12 12" aria-hidden="true">
                        <circle className="material-dotplot__swatch-dot material-dotplot__swatch-dot--few" cx="6" cy="6" r="4.5" />
                    </svg>
                    {`minder dan ${MIN_COMPANIES_TO_COMPARE}: te weinig om te vergelijken`}
                </span>
                {typeof reference === 'number' && (
                    <span className="material-dotplot__legend-item">
                        <svg className="material-dotplot__swatch" viewBox="0 0 12 12" aria-hidden="true">
                            <line className="material-dotplot__reference" x1="6" y1="0" x2="6" y2="12" />
                        </svg>
                        {`benchmark ${reference} ${unit}`}
                    </span>
                )}
            </div>
            <svg
                className="material-dotplot__chart"
                viewBox={`0 0 ${breedte} ${onder}`}
                role="img"
                aria-label={`Stippengrafiek: per contextkenmerk de besparing per huishouden, tegen de benchmark van ${reference} ${unit}.`}
            >
                {ticks.map((t) => (
                    <text key={t} className="material-dotplot__tick" x={x(t)} y={14} textAnchor="middle">{`${Math.round(t)} ${unit}`}</text>
                ))}
                {blokken.map(({ vraag, kopY, rijen, rijenBoven, rijenOnder }) => (
                    <g key={vraag.id}>
                        {ticks.map((t) => (
                            <line key={t} className="material-dotplot__grid" x1={x(t)} y1={rijenBoven} x2={x(t)} y2={rijenOnder} />
                        ))}
                        {typeof reference === 'number' && (
                            <line className="material-dotplot__reference" x1={x(reference)} y1={rijenBoven} x2={x(reference)} y2={rijenOnder} />
                        )}
                        <text className="material-dotplot__question" x={0} y={kopY + 18}>{vraag.label}</text>
                        {rijen.map(({ rij, cy }) => (
                            <g key={rij.key}>
                                <text className="material-dotplot__answer" x={16} y={cy + 4}>{rij.label}</text>
                                {typeof reference === 'number' && (
                                    <line className="material-dotplot__stem" x1={x(reference)} y1={cy} x2={x(rij.benchmarkLevel)} y2={cy} />
                                )}
                                <circle
                                    className={`material-dotplot__dot${rij.tooFew ? ' material-dotplot__dot--few' : ''}`}
                                    cx={x(rij.benchmarkLevel)}
                                    cy={cy}
                                    r={rij.tooFew ? 6 : 7}
                                >
                                    <title>{`${vraag.label} — ${rij.label}: ${rij.benchmarkLevel} ${unit} per huishouden, ${rij.companyCount} org.`}</title>
                                </circle>
                                <text className="material-dotplot__value" x={AS_RECHTS + 16} y={cy + 4}>
                                    {`${rij.benchmarkLevel} ${unit} · ${rij.companyCount} org`}
                                </text>
                            </g>
                        ))}
                    </g>
                ))}
            </svg>
        </div>
    )
}

export default MaterialContextDotPlot
